import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CheckoutLogin from '../components/checkout/CheckoutLogin';
import LoginModal from '../components/login/LoginModal';
import { LoginModalProvider } from '../context/LoginModalContext';
import CheckoutDates from '../components/checkout/CheckoutDates';
import CheckoutChildInfo, { ChildInfo } from '../components/checkout/CheckoutChildInfo';
import CheckoutParentInfo, { ParentInfo } from '../components/checkout/CheckoutParentInfo';
import CheckoutPayment, { CheckoutPaymentHandle } from '../components/checkout/CheckoutPayment';
import { useAnalytics } from '../context/AnalyticsContext';
import { useUserContext } from '../context/UserContext';
import { useNotifications } from '../context/NotificationContext';
import { useGetBooking, useUpdateBooking, useCompleteBooking, useCreateBooking, useApplyDiscount, useRemoveDiscount } from '../api';
import { CampDate } from '../utils/types';
import { CampFormatUtils } from '../utils/campFormatUtils';
import { Clock, Users, UserPlus, Tag, X } from 'lucide-react';
import './Checkout.css';

// Initialize Stripe - use environment variable or placeholder for development
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder'
);

const Checkout: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { trackPageView, trackEvent } = useAnalytics();
  const { user, isLoggedIn } = useUserContext();
  const { showError, showSuccess } = useNotifications();

  // Always fetch booking via GET to ensure fresh data
  const bookingUuid = searchParams.get('booking_uuid') || '';
  const { data: booking, isLoading, error, refetch: refetchBooking } = useGetBooking(bookingUuid);
  const updateBookingMutation = useUpdateBooking(bookingUuid);
  const completeBookingMutation = useCompleteBooking(bookingUuid);
  const createBookingMutation = useCreateBooking();
  const applyDiscountMutation = useApplyDiscount(bookingUuid);
  const removeDiscountMutation = useRemoveDiscount(bookingUuid);

  // Form state
  const [selectedDates, setSelectedDates] = useState<CampDate[]>([]);
  const [participants, setParticipants] = useState<ChildInfo[]>([{
    fullName: '',
    birthDate: '',
    medicalConditions: '',
  }]);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [parentInfo, setParentInfo] = useState<ParentInfo>({
    fullName: '',
    email: '',
    phone: '',
    relationship: '',
  });
  const [isPaymentReady, setIsPaymentReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [agreeToBookingTerms, setAgreeToBookingTerms] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginModalInitialStep, setLoginModalInitialStep] = useState<'email' | 'register' | 'reset-password'>('email');
  const [discountCode, setDiscountCode] = useState('');
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const [discountError, setDiscountError] = useState('');
  const [appliedDiscountCode, setAppliedDiscountCode] = useState<string | undefined>();
  const [discountAmount, setDiscountAmount] = useState<number | undefined>();
  const [originalPrice, setOriginalPrice] = useState<number | undefined>();

  // Track if form has been initialized from booking data
  const initializedFromBooking = useRef(false);

  // Track if we're already handling an expired booking to prevent loops
  const isCreatingNewBooking = useRef(false);

  // Ref for payment component to call confirmPayment
  const paymentRef = useRef<CheckoutPaymentHandle>(null);

  // Form initialization: initialize all form state from booking data
  useEffect(() => {
    if (!booking || initializedFromBooking.current) return;
    initializedFromBooking.current = true;

    // Initialize dates
    if (booking.camp_dates) {
      setSelectedDates(booking.camp_dates);
    }

    // Initialize participants
    if (booking.participants && booking.participants.length > 0) {
      setParticipants(booking.participants.map(p => ({
        fullName: p.name || '',
        birthDate: p.birthdate || '',
        medicalConditions: p.details || '',
      })));
    }

    // Initialize total price and discounts
    if (booking.total_price) {
      setTotalPrice(booking.total_price);
    }
    if (booking.original_price) {
      setOriginalPrice(booking.original_price);
    }
    if (booking.discount_code) {
      setAppliedDiscountCode(booking.discount_code);
      setDiscountAmount(booking.discount_amount);
    }

    // Initialize contact info (with user email fallback for logged-in users)
    const contact = booking.contact;
    setParentInfo({
      fullName: contact?.name || '',
      email: contact?.email || (isLoggedIn && user?.email) || '',
      phone: contact?.phone || '',
      relationship: contact?.relationship_to_participant || '',
    });
  }, [booking, isLoggedIn, user]);

  // Save all participants info and update price
  const saveParticipants = useCallback(async (updatedParticipants: ChildInfo[]) => {
    try {
      const result = await updateBookingMutation.mutateAsync({
        participants: updatedParticipants.map(p => ({
          id: p.id,
          name: p.fullName,
          birthdate: p.birthDate || undefined,
          details: p.medicalConditions || undefined,
        })),
      });
      // Update total price and discount info from response
      if (result.total_price !== undefined) {
        setTotalPrice(result.total_price);
      }
      setOriginalPrice(result.original_price);
      setDiscountAmount(result.discount_amount);
      setAppliedDiscountCode(result.discount_code);
    } catch (err) {
      console.error('Failed to save participant info:', err);
    }
  }, [updateBookingMutation]);

  // Handler for individual participant changes
  const handleParticipantChange = (index: number, info: ChildInfo) => {
    const newParticipants = [...participants];
    newParticipants[index] = info;
    setParticipants(newParticipants);
  };

  // Handler for participant blur - save all participants
  const handleParticipantBlur = useCallback(async () => {
    await saveParticipants(participants);
  }, [participants, saveParticipants]);

  // Calculate max allowed participants based on capacity and hard limit
  // Note: current_capacity is the number of bookings already made, so available = max_capacity - current_capacity
  const maxParticipants = Math.min(
    5,
    (booking?.camp.camp_dates?.length ?? 0) > 0
      ? Math.min(...(booking?.camp.camp_dates?.map(d => d.max_capacity - d.current_capacity) || []))
      : 5
  );

  const canAddParticipant = participants.length < maxParticipants;

  // Add a new participant
  const handleAddParticipant = async () => {
    console.log('Can add participant:', canAddParticipant);
    console.log('Current participants count:', participants.length);
    console.log('Max participants allowed:', maxParticipants);
    if (!canAddParticipant) return;
    const newParticipant: ChildInfo = {
      fullName: '',
      birthDate: '',
      medicalConditions: '',
    };
    const newParticipants = [...participants, newParticipant];
    setParticipants(newParticipants);
    // Save to backend to recalculate price (even with empty name, backend needs to know count)
    await saveParticipants(newParticipants);
  };

  // Remove a participant
  const handleRemoveParticipant = async (index: number) => {
    if (participants.length <= 1) return;
    const newParticipants = participants.filter((_, i) => i !== index);
    setParticipants(newParticipants);
    // Save to backend to recalculate price
    await saveParticipants(newParticipants);
  };

  // Save contact info on field blur
  const handleParentInfoBlur = useCallback(async () => {
    if (!parentInfo.fullName && !parentInfo.email) return;
    try {
      await updateBookingMutation.mutateAsync({
        contact: {
          name: parentInfo.fullName,
          email: parentInfo.email,
          phone: parentInfo.phone || undefined,
          relationship_to_participant: parentInfo.relationship || undefined,
        },
      });
    } catch (err) {
      console.error('Failed to save contact info:', err);
    }
  }, [parentInfo, updateBookingMutation]);

  // Apply discount code
  const handleApplyDiscount = async () => {
    const code = discountCode.trim();
    if (!code) return;

    setIsApplyingDiscount(true);
    setDiscountError('');

    try {
      const result = await applyDiscountMutation.mutateAsync(code);
      if (result.total_price !== undefined) {
        setTotalPrice(result.total_price);
      }
      if (result.original_price !== undefined) {
        setOriginalPrice(result.original_price);
      }
      setAppliedDiscountCode(result.discount_code);
      setDiscountAmount(result.discount_amount);
      setDiscountCode('');
    } catch (err) {
      console.log(err);
      setDiscountError(err instanceof Error ? err.message : 'Invalid discount code');
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  // Remove discount code
  const handleRemoveDiscount = async () => {
    try {
      const result = await removeDiscountMutation.mutateAsync();
      if (result.total_price !== undefined) {
        setTotalPrice(result.total_price);
      }
      setOriginalPrice(result.original_price);
      setAppliedDiscountCode(undefined);
      setDiscountAmount(undefined);
    } catch (err) {
      console.error('Failed to remove discount:', err);
    }
  };

  // Analytics: track page view and checkout event
  useEffect(() => {
    trackPageView('/checkout', 'Checkout');
    if (booking) {
      trackEvent('checkout_page_viewed', {
        camp_id: booking.camp.id,
        booking_uuid: booking.uuid
      });
    }
  }, [booking, trackPageView, trackEvent]);

  // Navigation: handle all redirects in one effect
  useEffect(() => {
    if (!bookingUuid) {
      navigate('/search');
      return;
    }
    if (error) {
      navigate('/search');
      return;
    }
    if (booking?.status === 'completed' || booking?.status === 'confirmed') {
      navigate(`/confirmation?booking_uuid=${bookingUuid}`, {
        state: { booking },
        replace: true,
      });
      return;
    }
    // Handle expired bookings by creating a new one
    if (booking?.status === 'expired' && !isCreatingNewBooking.current) {
      isCreatingNewBooking.current = true;
      const createNewBooking = async () => {
        try {
          const newBooking = await createBookingMutation.mutateAsync({
            camp_id: booking.camp.id,
            camp_date_ids: booking.camp_dates.map(d => String(d.id)),
          });
          navigate(`/checkout?booking_uuid=${newBooking.uuid}`, {
            replace: true,
          });
        } catch (err) {
          console.error('Failed to create new booking:', err);
          isCreatingNewBooking.current = false;
          navigate('/search');
        }
      };
      createNewBooking();
    }
  }, [bookingUuid, error, booking, navigate, createBookingMutation]);

  // Date management handlers - update both local state and backend
  const updateBookingDates = async (newDateIds: (string | number)[]) => {
    try {
      console.log('Updating booking dates to:', newDateIds);
      const result = await updateBookingMutation.mutateAsync({ camp_date_ids: newDateIds.map(id => Number(id)) });
      // Update total price and discount info from response
      if (result.total_price !== undefined) {
        setTotalPrice(result.total_price);
      }
      setOriginalPrice(result.original_price);
      setDiscountAmount(result.discount_amount);
      setAppliedDiscountCode(result.discount_code);
      refetchBooking();
    } catch (err) {
      showError('Failed to update booking dates', err instanceof Error ? err : new Error('Update failed'));
    }
  };

  const handleDateRemove = (dateId: string) => {
    if (selectedDates.length > 1) {
      // Use string comparison to handle type mismatch between API (number) and DOM (string)
      const newDates = selectedDates.filter(d => String(d.id) !== String(dateId));
      setSelectedDates(newDates);
      updateBookingDates(newDates.map(d => d.id));
    }
  };

  const handleDateAdd = (dateId: string) => {
    const availableDates = booking?.camp.camp_dates;
    if (!availableDates || availableDates.length === 0) return;

    // Use string comparison to handle type mismatch between API (number) and DOM (string)
    const newDate = availableDates.find(d => String(d.id) === String(dateId));
    if (!newDate) return;

    const newDates = [...selectedDates, newDate];
    setSelectedDates(newDates);
    updateBookingDates(newDates.map(d => d.id));
  };

  // Form validation
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validate all participants - name and birthday required
    participants.forEach((participant, index) => {
      if (!participant.fullName.trim()) {
        errors[`participant.${index}.fullName`] = 'Child\'s name is required';
      }
      if (!participant.birthDate) {
        errors[`participant.${index}.birthDate`] = 'Birth date is required';
      }
    });

    // Parent info validation - name, email, and relationship required
    if (!parentInfo.fullName.trim()) {
      errors['parent.fullName'] = 'Your name is required';
    }
    if (!parentInfo.email.trim()) {
      errors['parent.email'] = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(parentInfo.email)) {
      errors['parent.email'] = 'Invalid email format';
    }
    if (!parentInfo.relationship) {
      errors['parent.relationship'] = 'Relationship is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      showError('Please fill in all required fields', new Error('Validation failed'));
      return;
    }

    if (!isPaymentReady) {
      showError('Please enter valid payment details', new Error('Payment not ready'));
      return;
    }

    if (!paymentRef.current) {
      showError('Payment system not ready', new Error('Payment ref not available'));
      return;
    }

    setIsSubmitting(true);

    try {
      trackEvent('checkout_submitted', {
        booking_uuid: bookingUuid,
        camp_id: booking?.camp.id,
        total_price: booking?.total_price,
        date_count: selectedDates.length,
        participant_count: participants.length,
      });

      // Update booking one more time to ensure the correct price is set in Stripe
      await updateBookingMutation.mutateAsync({
        contact: {
          name: parentInfo.fullName,
          email: parentInfo.email,
          phone: parentInfo.phone || undefined,
          relationship_to_participant: parentInfo.relationship || undefined,
        },
        participants: participants.map(p => ({
          id: p.id,
          name: p.fullName,
          birthdate: p.birthDate || undefined,
          details: p.medicalConditions || undefined,
        })),
      });
    } catch (err) {
      showError('Error in creating the booking, payment has not been processed. Please try again.', err instanceof Error ? err : new Error('Booking update failed'));
      setIsSubmitting(false);
      return;
    }
    try {
      // Confirm the payment with Stripe
      const { paymentIntent, error: paymentError } = await paymentRef.current.confirmPayment();

      if (paymentError) {
        trackEvent('payment_failed', {
          booking_uuid: bookingUuid,
          error: paymentError,
        });
        showError(paymentError, new Error('Payment failed'));
        setIsSubmitting(false);
        return;
      }

      trackEvent('payment_succeeded', {
        booking_uuid: bookingUuid,
        payment_intent_id: paymentIntent?.id,
      });
    } catch (err) {
      showError('Payment confirmation failed. Please try again.', err instanceof Error ? err : new Error('Payment confirmation failed'));
      setIsSubmitting(false);
      return;
    }
    try {
      // Then complete the booking on the backend
      const completedBooking = await completeBookingMutation.mutateAsync({
        contact: {
          name: parentInfo.fullName,
          email: parentInfo.email,
          phone: parentInfo.phone || undefined,
          relationship_to_participant: parentInfo.relationship || undefined,
        },
        participants: participants.map(p => ({
          id: p.id,
          name: p.fullName,
          birthdate: p.birthDate || undefined,
          details: p.medicalConditions || undefined,
        })),
      });

      trackEvent('checkout_completed', {
        booking_uuid: bookingUuid,
        camp_id: booking?.camp.id,
        total_price: completedBooking.total_price,
      });

      showSuccess('Booking completed successfully!');

      // Navigate to confirmation page with booking data
      navigate(`/confirmation?booking_uuid=${bookingUuid}`, {
        state: { booking: completedBooking }
      });
    } catch (err) {
      trackEvent('checkout_failed', {
        booking_uuid: bookingUuid,
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      showError('Failed to complete booking. Payment has been processed, but there was an issue finalizing your booking. Please contact info@aika.camp.', err instanceof Error ? err : new Error('Booking completion failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading while fetching booking
  if (isLoading || !booking) {
    return (
      <div className="checkout-page">
        <Header />
        <main className="checkout-main">
          <div className="checkout-container">
            <div className="checkout-loading">Loading...</div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const camp = booking.camp;

  return (
    <div className="checkout-page">
      <Header />

      <main className="checkout-main">
        <div className="checkout-container">
          <h1 className="checkout-title">Complete Your Camp Booking</h1>

          <form onSubmit={handleSubmit} className="checkout-form">
            <div className="checkout-grid">
              {/* Desktop Sidebar - contains camp summary and footer */}
              <div className="checkout-sidebar">
                <div className="checkout-summary-card">
                  <div className="checkout-camp-photo">
                    <img
                      src={camp.images?.[0]?.url || camp.photo_url || '/placeholder-camp.jpg'}
                      alt={camp.name}
                      className="checkout-camp-image"
                    />
                  </div>
                  <div className="checkout-camp-info">
                    <h2 className="checkout-camp-name">{camp.name}</h2>
                    {camp.description && (
                      <p className="checkout-camp-description">
                        {camp.description.length > 150
                          ? `${camp.description.substring(0, 150)}...`
                          : camp.description}
                      </p>
                    )}
                    <div className="checkout-camp-details">
                      <div className="checkout-camp-detail-item">
                        <Clock className="icon icon--sm" />
                        <span>Hours: {CampFormatUtils.formatTimeRange(camp.start_time, camp.end_time)}</span>
                      </div>
                      <div className="checkout-camp-detail-item">
                        <Users className="icon icon--sm" />
                        <span>Ages: {CampFormatUtils.formatAgeRange(camp.min_age, camp.max_age)}</span>
                      </div>
                    </div>
                  </div>
                  {/* Dates in summary card */}
                  <div className="checkout-summary-dates">
                    <h3 className="checkout-summary-section-title">Selected Dates</h3>
                    <CheckoutDates
                      camp={camp}
                      selectedDates={selectedDates}
                      onDateRemove={handleDateRemove}
                      onDateAdd={handleDateAdd}
                    />
                  </div>
                  {/* Discount Code */}
                  <div className="checkout-discount">
                    <h3 className="checkout-summary-section-title">Discount Code</h3>
                    {appliedDiscountCode ? (
                      <div className="checkout-discount-applied">
                        <div className="checkout-discount-applied-info">
                          <Tag className="icon icon--sm" />
                          <span className="checkout-discount-applied-code">{appliedDiscountCode}</span>
                          <span className="checkout-discount-applied-amount">-${discountAmount}</span>
                        </div>
                        <button
                          type="button"
                          className="checkout-discount-remove"
                          onClick={handleRemoveDiscount}
                        >
                          <X className="icon icon--sm" />
                        </button>
                      </div>
                    ) : (
                      <div className="checkout-discount-row">
                        <input
                          type="text"
                          className={`form-input checkout-discount-input${discountError ? ' form-input--error' : ''}`}
                          placeholder="Enter code"
                          value={discountCode}
                          onChange={(e) => {
                            setDiscountCode(e.target.value);
                            setDiscountError('');
                          }}
                          disabled={isApplyingDiscount}
                        />
                        <button
                          type="button"
                          className="btn btn--secondary btn--md checkout-discount-apply"
                          onClick={handleApplyDiscount}
                          disabled={isApplyingDiscount || !discountCode.trim()}
                        >
                          {isApplyingDiscount ? 'Applying...' : 'Apply'}
                        </button>
                      </div>
                    )}
                    {discountError && (
                      <p className="form-error checkout-discount-error">{discountError}</p>
                    )}
                  </div>
                  {/* Total Price */}
                  <div className="checkout-summary-total">
                    <span className="checkout-summary-total-label">Total</span>
                    <div className="checkout-summary-total-prices">
                      {originalPrice != null && originalPrice !== totalPrice && (
                        <span className="checkout-summary-original-price">${originalPrice}</span>
                      )}
                      <span className="checkout-summary-total-amount">${totalPrice || booking.total_price}</span>
                    </div>
                    {appliedDiscountCode && (
                      <span className="checkout-summary-discount-note">
                        {appliedDiscountCode} applied
                      </span>
                    )}
                  </div>
                  {/* Booking Terms and Conditions */}
                  <div className="checkout-terms">
                    <div className="terms-checkbox">
                      <input
                        type="checkbox"
                        id="booking-terms"
                        checked={agreeToBookingTerms}
                        onChange={(e) => setAgreeToBookingTerms(e.target.checked)}
                        disabled={isSubmitting}
                      />
                      <label htmlFor="booking-terms" className="terms-label">
                        I agree to the <a
                          href="/booking-terms"
                          onClick={(e) => {
                            e.preventDefault();
                            window.open('/booking-terms', 'booking-terms', 'width=800,height=600,scrollbars=yes,resizable=yes');
                          }}
                          className="terms-link"
                        >
                          booking terms & conditions
                        </a>.
                      </label>
                    </div>
                  </div>
                  {/* Submit Button */}
                  <div className="checkout-submit">
                    <button
                      type="submit"
                      className="btn btn--primary btn--lg btn--block checkout-submit-btn"
                      disabled={isSubmitting || !agreeToBookingTerms}
                    >
                      {isSubmitting ? 'Processing...' : 'Complete Booking'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Mobile: Camp info at top */}
              <div className="checkout-mobile-camp">
                <div className="checkout-summary-card">
                  <div className="checkout-camp-photo">
                    <img
                      src={camp.images?.[0]?.url || camp.photo_url || '/placeholder-camp.jpg'}
                      alt={camp.name}
                      className="checkout-camp-image"
                    />
                  </div>
                  <div className="checkout-camp-info">
                    <h2 className="checkout-camp-name">{camp.name}</h2>
                    {camp.description && (
                      <p className="checkout-camp-description">
                        {camp.description.length > 150
                          ? `${camp.description.substring(0, 150)}...`
                          : camp.description}
                      </p>
                    )}
                    <div className="checkout-camp-details">
                      <div className="checkout-camp-detail-item">
                        <Clock className="icon icon--sm" />
                        <span>Hours: {CampFormatUtils.formatTimeRange(camp.start_time, camp.end_time)}</span>
                      </div>
                      <div className="checkout-camp-detail-item">
                        <Users className="icon icon--sm" />
                        <span>Ages: {CampFormatUtils.formatAgeRange(camp.min_age, camp.max_age)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Login Section */}
              <div className="checkout-forms-login">
                <CheckoutLogin
                  onLoginSuccess={() => {
                    setShowLoginModal(false);
                    showSuccess('Successfully logged in!');
                  }}
                  onRegisterClick={() => {
                    setLoginModalInitialStep('register');
                    setShowLoginModal(true);
                  }}
                  onForgotPasswordClick={() => {
                    setLoginModalInitialStep('reset-password');
                    setShowLoginModal(true);
                  }}
                />
              </div>

              {/* Mobile: Selected Dates - Below login */}
              <div className="checkout-mobile-dates">
                <section className="checkout-section-card">
                  <h2 className="checkout-section-title">Selected Dates</h2>
                  <CheckoutDates
                    camp={camp}
                    selectedDates={selectedDates}
                    onDateRemove={handleDateRemove}
                    onDateAdd={handleDateAdd}
                  />
                </section>
              </div>

              {/* Participants Info Section */}
              <div className="checkout-forms-participants">
                <section className="checkout-section-card">
                  <h2 className="checkout-section-title">Participant Information</h2>
                  <p className="checkout-section-description">
                    Please provide details for each child attending the camp.
                  </p>

                  {participants.map((participant, index) => (
                    <CheckoutChildInfo
                      key={index}
                      index={index}
                      childInfo={participant}
                      onChange={(info) => handleParticipantChange(index, info)}
                      onBlur={handleParticipantBlur}
                      onRemove={() => handleRemoveParticipant(index)}
                      canRemove={participants.length > 1}
                      errors={{
                        fullName: formErrors[`participant.${index}.fullName`],
                        birthDate: formErrors[`participant.${index}.birthDate`],
                      }}
                    />
                  ))}

                  <button
                    type="button"
                    className="btn btn--secondary btn--md checkout-add-participant"
                    onClick={handleAddParticipant}
                    disabled={!canAddParticipant}
                  >
                    <UserPlus className="icon icon--sm" />
                    Add Another Participant
                  </button>
                  {!canAddParticipant && (
                    <p className="checkout-participant-limit-message">
                      {participants.length >= 5
                        ? 'Maximum of 5 participants allowed'
                        : `Only ${maxParticipants} spot${maxParticipants === 1 ? '' : 's'} available for the selected date${selectedDates.length > 1 ? 's' : ''}`}
                    </p>
                  )}
                </section>
              </div>

              {/* Parent/Guardian Info Section */}
              <div className="checkout-forms-parent">
                <section className="checkout-section-card">
                  <h2 className="checkout-section-title">Parent/Guardian Information</h2>
                  <CheckoutParentInfo
                    parentInfo={parentInfo}
                    onChange={setParentInfo}
                    onBlur={handleParentInfoBlur}
                    errors={{
                      fullName: formErrors['parent.fullName'],
                      email: formErrors['parent.email'],
                      phone: formErrors['parent.phone'],
                      relationship: formErrors['parent.relationship'],
                    }}
                  />
                </section>
              </div>

              {/* Payment Section */}
              <div className="checkout-forms-payment">
                <section className="checkout-section-card">
                  <h2 className="checkout-section-title">Payment Information</h2>
                  {booking.client_secret ? (
                    <Elements stripe={stripePromise} options={{ clientSecret: booking.client_secret }}>
                      <CheckoutPayment
                        ref={paymentRef}
                        onPaymentReady={setIsPaymentReady}
                      />
                    </Elements>
                  ) : (
                    <div className="checkout-payment-loading">Loading payment form...</div>
                  )}
                </section>
              </div>

              {/* Mobile: Total, Terms, and Submit at bottom */}
              <div className="checkout-mobile-footer">
                <div className="checkout-footer-card">
                  {/* Discount Code - Mobile */}
                  <div className="checkout-discount">
                    <h3 className="checkout-summary-section-title">Discount Code</h3>
                    {appliedDiscountCode ? (
                      <div className="checkout-discount-applied">
                        <div className="checkout-discount-applied-info">
                          <Tag className="icon icon--sm" />
                          <span className="checkout-discount-applied-code">{appliedDiscountCode}</span>
                          <span className="checkout-discount-applied-amount">-${discountAmount}</span>
                        </div>
                        <button
                          type="button"
                          className="checkout-discount-remove"
                          onClick={handleRemoveDiscount}
                        >
                          <X className="icon icon--sm" />
                        </button>
                      </div>
                    ) : (
                      <div className="checkout-discount-row">
                        <input
                          type="text"
                          className={`form-input checkout-discount-input${discountError ? ' form-input--error' : ''}`}
                          placeholder="Enter code"
                          value={discountCode}
                          onChange={(e) => {
                            setDiscountCode(e.target.value);
                            setDiscountError('');
                          }}
                          disabled={isApplyingDiscount}
                        />
                        <button
                          type="button"
                          className="btn btn--secondary btn--md checkout-discount-apply"
                          onClick={handleApplyDiscount}
                          disabled={isApplyingDiscount || !discountCode.trim()}
                        >
                          {isApplyingDiscount ? 'Applying...' : 'Apply'}
                        </button>
                      </div>
                    )}
                    {discountError && (
                      <p className="form-error checkout-discount-error">{discountError}</p>
                    )}
                  </div>
                  <div className="checkout-summary-total">
                    <span className="checkout-summary-total-label">Total</span>
                    <div className="checkout-summary-total-prices">
                      {originalPrice != null && originalPrice !== totalPrice && (
                        <span className="checkout-summary-original-price">${originalPrice}</span>
                      )}
                      <span className="checkout-summary-total-amount">${totalPrice || booking.total_price}</span>
                    </div>
                    {appliedDiscountCode && (
                      <span className="checkout-summary-discount-note">
                        {appliedDiscountCode} applied
                      </span>
                    )}
                  </div>

                  <div className="checkout-terms">
                    <div className="terms-checkbox">
                      <input
                        type="checkbox"
                        id="booking-terms-mobile"
                        checked={agreeToBookingTerms}
                        onChange={(e) => setAgreeToBookingTerms(e.target.checked)}
                        disabled={isSubmitting}
                      />
                      <label htmlFor="booking-terms-mobile" className="terms-label">
                        I agree to the <a
                          href="/booking-terms"
                          onClick={(e) => {
                            e.preventDefault();
                            window.open('/booking-terms', 'booking-terms', 'width=800,height=600,scrollbars=yes,resizable=yes');
                          }}
                          className="terms-link"
                        >
                          booking terms & conditions
                        </a>.
                      </label>
                    </div>
                  </div>

                  <div className="checkout-submit">
                    <button
                      type="submit"
                      className="btn btn--primary btn--lg btn--block checkout-submit-btn"
                      disabled={isSubmitting || !agreeToBookingTerms}
                    >
                      {isSubmitting ? 'Processing...' : 'Complete Booking'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </main>

      <Footer />

      {/* Login/Register Modal */}
      {showLoginModal && (
        <LoginModalProvider initialStep={loginModalInitialStep}>
          <LoginModal
            onClose={() => setShowLoginModal(false)}
            onLogin={() => {
              setShowLoginModal(false);
              showSuccess('Successfully logged in!');
            }}
            onRegister={() => {
              setShowLoginModal(false);
              showSuccess('Account created successfully!');
            }}
          />
        </LoginModalProvider>
      )}
    </div>
  );
};

export default Checkout;
